import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import ErrorIcon from "@mui/icons-material/Error";
import Alert from "../../../components/UI/alert";
import ModalCloseButton from "../../../components/UI/ModalCloseButton";
import AuthCode from "react-auth-code-input";
import useAxiosPrivate from "../../../utils/hooks/useAxiosPrivate";
import { useDispatch, useSelector } from "react-redux";
import { setEmail } from "../../../store/actions/authActions";
import {
    centeredModalBoxSx,
    modalBackdropProps,
} from "../../../utils/styles/modal.overlay";
import styles from "../setting.module.css";

const modalSx = centeredModalBoxSx({
    width: { xs: "min(calc(100% - 32px), 520px)", sm: 560 },
    overflow: "hidden",
    p: 4,
});

export default function AuthenticationBeforeModal(props) {
    const { setOpen, open, setOpenedEmail, setSuccessEmailForAdmin } = props;
    const changeEmail = useSelector((state) => state.auth.changeEmail);
    const email = useSelector((state) => state.auth.email);
    const displayEmail = changeEmail || email;
    const axios = useAxiosPrivate();
    const dispatch = useDispatch();
    const [result, setResult] = useState("");
    const [counter, setCount] = useState(0);
    const [authCodeKey, setAuthCodeKey] = useState(0);
    const [alert, setAlert] = useState({
        showAlert: false,
        severity: "success",
        message: "",
    });

    const loadData = async () => {
        try {
            await axios.get("/pincode/request/EMAIL_CHANGE");
        } catch (error) {
            if (error.response && error.response?.data) {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: error.response?.data?.message,
                });
                setTimeout(() => {
                    setAlert({
                        showAlert: false,
                        severity: "error",
                        message: error.response?.data?.message,
                    });
                }, 5000);
            } else {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: "Something was wrong!",
                });
                setTimeout(() => {
                    setAlert({
                        showAlert: false,
                        severity: "error",
                        message: "Something was wrong!",
                    });
                }, 5000);
            }
        }
    };

    const ensureEmailLoaded = async () => {
        if (changeEmail || email) {
            return;
        }

        try {
            const { data } = await axios.get("/user/profile");
            if (data?.data?.email) {
                dispatch(setEmail(data.data.email));
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (open) {
            setOpenedEmail(false);
            setResult("");
            setAlert({
                showAlert: false,
                severity: "success",
                message: "",
            });
            setCount(60);
            ensureEmailLoaded();
        }
        // eslint-disable-next-line
    }, [open]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        if (counter > 0) {
            const timer = setTimeout(() => setCount(counter - 1), 1000);
            return () => clearTimeout(timer);
        }

        return undefined;
        // eslint-disable-next-line
    }, [counter, open]);

    const resendCode = () => {
        setResult("");
        setAuthCodeKey((key) => key + 1);
        setCount(60);
        loadData();
    };

    useEffect(() => {
        if (result?.length === 6) {
            submit();
        }
        // eslint-disable-next-line
    }, [result]);

    const handleOnChange = (res) => {
        setResult(res);
    };

    const submit = async () => {
        try {
            const { data } = await axios.post("/pincode/verify", {
                pincode: result,
            });
            if (data.message === "PIN Code verified!") {
                await axios.post("/user/update_email", {
                    verification_id: data?.verification_id,
                });
                setTimeout(() => {
                    setSuccessEmailForAdmin(true);
                }, 1000);
            } else {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: data.message,
                });
                setTimeout(() => {
                    setAlert({
                        showAlert: false,
                        severity: "error",
                        message: data.message,
                    });
                }, 7000);
            }
        } catch (error) {
            if (error.response && error.response?.data) {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: error.response?.data?.message,
                });
                setTimeout(() => {
                    setAlert({
                        showAlert: false,
                        severity: "error",
                        message: error.response?.data?.message,
                    });
                }, 7000);
            } else {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: "Something was wrong!",
                });
                setTimeout(() => {
                    setAlert({
                        showAlert: false,
                        severity: "error",
                        message: "Something was wrong!",
                    });
                }, 7000);
            }
        }
    };

    const handleClose = () => setOpen(false);

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            BackdropProps={modalBackdropProps}
            sx={{ zIndex: 1400 }}
        >
            <Box sx={modalSx}>
                <div className={styles.authModalContent}>
                    <div className={styles.authModalCloseRow}>
                        <ModalCloseButton
                            style={{ position: "static" }}
                            onClick={handleClose}
                        />
                    </div>

                    <p id="modal-modal-title" className={styles.AuthenticationModelText}>
                        Authentication
                    </p>

                    <p className={styles.authInstructionText}>
                        Authentication code has been sent to your email--
                    </p>
                    <p className={styles.authEmailText}>{displayEmail || "---"}</p>
                    <p className={styles.authInstructionText}>
                        Please enter the code below
                    </p>

                    <div className={styles.authCodeWrapper}>
                        <div className={styles.authCodeBlock}>
                            <AuthCode
                                key={`auth-before-${open ? "open" : "closed"}-${authCodeKey}`}
                                onChange={handleOnChange}
                                containerClassName={styles.authCodeContainer}
                                inputClassName={styles.authCodeInput}
                                allowedCharacters="numeric"
                            />

                            {alert.showAlert && alert.message === "PIN Code invalid" ? (
                                <div className={styles.errorMessage}>
                                    <ErrorIcon className={styles.errorIcon} />
                                    <span>Incorrect code. Please resend the code.</span>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className={styles.authFooter}>
                        <p className={styles.HaveRecevedText}>Haven&apos;t received?</p>
                        {counter > 0 ? (
                            <p className={styles.authResendText}>
                                Resend the code after{" "}
                                <span className={styles.ReSendText}>{counter}s</span>
                            </p>
                        ) : (
                            <p
                                className={styles.ReSendText}
                                onClick={resendCode}
                            >
                                Resend the code
                            </p>
                        )}
                    </div>

                    {alert.showAlert && alert.message !== "PIN Code invalid" ? (
                        <Alert
                            showAlert={alert.showAlert}
                            severity={alert.severity}
                            message={alert.message}
                            setAlert={setAlert}
                        />
                    ) : null}
                </div>
            </Box>
        </Modal>
    );
}
